import { Group, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { i18nKeys, useTranslation } from 'locales';
import type { JSX } from 'react';
import { isNullish } from 'remeda';

import { FIELD_TYPE_TO_ICON, useAutomationWizardController } from '../../controller';
import type { AutomationFieldType } from '../../controller/field-types';

import type { ActionCardProps } from './ActionCard';
import { renderFieldItem, selectedFieldStyle } from './components';

export const CopyValueToField = ({ actionIndex }: ActionCardProps) => {
  const { t } = useTranslation();
  const {
    form,
    actions: { getDestinationFieldType, getDestinationFieldProps },
  } = useAutomationWizardController();

  const fieldType = getDestinationFieldType(actionIndex);

  const inputProps = {
    autoFocus: true,
    label: t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.VALUE_LABEL),
    leftSection: isNullish(fieldType) ? null : FIELD_TYPE_TO_ICON[fieldType],
    ...form.getInputProps(`actions.${actionIndex}.value`),
  };

  const typeToInputField: Record<AutomationFieldType, JSX.Element> = {
    string: (
      <TextInput
        placeholder={t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.VALUE_PLACEHOLDER_TEXT)}
        {...inputProps}
      />
    ),
    select: <></>,
    boolean: (
      <Select
        placeholder={t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.VALUE_PLACEHOLDER_SELECT)}
        data={[
          { value: 'true', label: t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.TRUE) },
          { value: 'false', label: t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.FALSE) },
        ]}
        {...inputProps}
      />
    ),
    number: (
      <NumberInput
        hideControls
        placeholder={t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.VALUE_PLACEHOLDER_TEXT)}
        {...inputProps}
      />
    ),
    date: <DatePickerInput w="100%" {...inputProps} />,
    id: (
      <TextInput
        placeholder={t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.VALUE_PLACEHOLDER_TEXT)}
        {...inputProps}
      />
    ),
    array: (
      <TextInput
        placeholder={t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.VALUE_PLACEHOLDER_ARRAY)}
        {...inputProps}
      />
    ),
  };

  return (
    <Stack>
      <Text size="sm" fw={500}>
        {t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.COPY_VALUE_TO_FIELD)}
      </Text>
      <Group gap="xl" align="end">
        <Select
          label={t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.FIELD_LABEL)}
          placeholder={t(i18nKeys.AUTOMATION.ACTIONS_CONFIG.FIELD_PLACEHOLDER)}
          renderOption={renderFieldItem}
          allowDeselect={false}
          clearable
          searchable
          classNames={{
            option: selectedFieldStyle,
          }}
          {...form.getInputProps(`actions.${actionIndex}.destinationField`)}
          {...getDestinationFieldProps(actionIndex)}
        />
        {typeToInputField[fieldType!]}
      </Group>
    </Stack>
  );
};
